/**
 * Zod Schemas for Tag Showcases API
 * /api/recommendations/tag-showcases endpoint schemas
 *
 * Returns personalized showcases grouped by user's preferred tags
 * with infinite scroll pagination
 */

import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const TagShowcasesQuerySchema = z.object({
  type: z.enum(['user', 'session']),
  userId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  sessionId: z.string().optional(),
  anonymousId: z.string().optional(),
  days: z.string().optional().default('30').transform((val) => parseInt(val, 10)),
  offset: z.string().optional().default('0').transform((val) => parseInt(val, 10)),
  limit: z.string().optional().default('5').transform((val) => parseInt(val, 10)),
  listingsPerTag: z.string().optional().default('12').transform((val) => parseInt(val, 10)),
  minListingsPerTag: z.string().optional().default('4').transform((val) => parseInt(val, 10)),
  // Tags already rendered on previous pages. Sent as repeated params, because a
  // location tag carries a comma ("Dallas, TX"): split as CSV it came back as
  // "Dallas" and "TX", missed the seen set, and the For You page drew the same
  // row again on the next page. A bare string is still split on commas for a
  // client loaded before the change. Next.js delivers repeats as string[].
  seenTags: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .default([])
    .transform(v => (Array.isArray(v) ? v : v ? v.split(',') : []).map(s => s.trim()).filter(Boolean)),
});

export type TagShowcasesQuery = z.infer<typeof TagShowcasesQuerySchema>;

// =============================================================================
// SHOWCASE SCHEMAS
// =============================================================================

export const TagShowcaseSchema = z.object({
  tag: z.string(),
  title: z.string().optional(),
  viewCount: z.coerce.number(),
  listings: z.array(z.any()),
  /**
   * The category group the row's listings were drawn from, when it was held
   * to one. A client paging the row further must pass it on, or page two
   * mixes horror books into a horror-movies row again.
   */
  categoryGroupId: z.number().int().positive().optional(),
  /**
   * Set on a brand row: the indexed brand value its listings were drawn by.
   * A client paging the row, or linking to See All, filters by `brand`, not
   * by `tag` (which is then the row key `brand:<value>`).
   */
  brand: z.string().optional(),
});

export type TagShowcase = z.infer<typeof TagShowcaseSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

export const TagShowcasesSuccessResponseSchema = z.object({
  success: z.literal(true),
  showcases: z.array(TagShowcaseSchema),
  total: z.number(),
  hasMore: z.boolean(),
  totalAvailable: z.number(),
  /**
   * Which ranking arm served these rows: a variant id, or `control`. The client
   * remembers it for the visit, so the events For You produces carry the same
   * label as the ones the home page produces.
   */
  rankingVariant: z.string().optional(),
});

export type TagShowcasesSuccessResponse = z.infer<typeof TagShowcasesSuccessResponseSchema>;

export const TagShowcasesErrorResponseSchema = z.object({
  success: z.literal(false),
  showcases: z.array(z.any()).default([]),
  total: z.number().default(0),
  error: z.string(),
});

export type TagShowcasesErrorResponse = z.infer<typeof TagShowcasesErrorResponseSchema>;

export type TagShowcasesResponse = TagShowcasesSuccessResponse | TagShowcasesErrorResponse;
